import {
  type BillingInterval,
  PLAN_DISPLAY,
  type SelfServePlanKey,
} from "@giftforward/entitlements";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { useCallback, useEffect, useState } from "react";
import BillingIntervalPills from "@/components/plan/BillingIntervalPills";
import SubscriptionCardForm from "@/components/plan/SubscriptionCardForm";
import { PLAN_DRAWER_STRINGS, PLAN_INTERVAL_STRINGS } from "@/constants/Plans";
import { usePlanActions } from "@/hooks/usePlanActions";
import { usePlans } from "@/hooks/usePlans";
import { formatPlanCharge, selectedOffer } from "@/utils/plan-price-helper";
import { userFacingErrorMessage } from "@/utils/userFacingError-helper";

/**
 * The paid-plan (Pro / Pro Plus) payment step of onboarding.
 *
 * This used to render our OWN card fields — number, expiry, CVC — validate them, wait 900ms on a
 * `setTimeout`, and call `onPaymentSuccess()`. Nothing was sent anywhere: no network call, no
 * charge, no subscription. A client typed a real card, was told it worked, and landed in the app on
 * Starter. It also put a raw PAN in our DOM and React state, which is the one thing every other
 * card surface in this app avoids by handing the fields to Stripe.
 *
 * Now it is the same machinery as the account-page upgrade: an organization SetupIntent, Stripe's
 * Payment Element, and `POST /tier/upgrade` with the PaymentMethod the confirmed intent returns.
 * The card never touches our page.
 *
 * The ORGANIZATION MUST EXIST before this opens — a subscription bills the organization's Stripe
 * Customer, and there is nothing to mint an intent against otherwise. The wizard's SETUP step
 * creates it immediately before opening this, which is why this drawer takes no plan-creation
 * responsibility and no readiness flag: by the time it can open, the organization is there.
 *
 * Monthly / Annual pills when the plan is sold both ways (Pro), Annual by default — the same choice
 * as the account-page drawer. The interval lives in the wizard's form data so the payment and team
 * steps say what was actually bought. Amounts come from the catalogue's offers (GET /plans), which
 * the organization can read by now.
 *
 * Success hands straight back to the wizard, which advances to the team step. The confirmation of
 * the charge lives there, on the screen that offers the seats it just bought, rather than in a
 * drawer that has to stay open to be read.
 */

interface OnboardingPaymentDrawerProps {
  /** The paid plan picked at the plan step; null renders nothing. */
  plan: SelfServePlanKey | null;
  interval: BillingInterval;
  onIntervalChange: (interval: BillingInterval) => void;
  isOpen: boolean;
  onClose: () => void;
  onPaymentSuccess: () => void;
}

const GENERIC_PAYMENT_ERROR = "Something went wrong processing your payment. Please try again.";

export function OnboardingPaymentDrawer({
  plan,
  interval,
  onIntervalChange,
  isOpen,
  onClose,
  onPaymentSuccess,
}: OnboardingPaymentDrawerProps) {
  const { upgrade } = usePlanActions();
  const [error, setError] = useState<string | null>(null);
  const isProcessing = upgrade.isPending;
  const { byKey, isLoading: plansLoading } = usePlans();
  const offers = plan ? (byKey(plan)?.offers ?? []) : [];
  const offer = selectedOffer(offers, interval);

  useEffect(() => {
    if (isOpen) setError(null);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    // A charge in flight must not be abandoned by a stray click — the same rule the account-page
    // drawer follows. Closing at any other time is safe: it returns to the payment step, which
    // offers both reopening this and taking Starter instead.
    if (isProcessing) return;
    onClose();
  }, [isProcessing, onClose]);

  /**
   * The card is saved against the organization; now buy the plan with it.
   *
   * A decline resolves as `{ status: "error" }` with HTTP 200, so it renders inline and the drawer
   * stays open — the client can try another card without losing their place in onboarding.
   */
  const handleCardSaved = useCallback(
    async (paymentMethodId: string) => {
      if (!plan || !offer) return;
      setError(null);
      try {
        const result = await upgrade.mutateAsync({
          planId: plan,
          interval: offer.interval,
          paymentMethodId,
        });
        if (result.status === "error") {
          setError(result.message);
          return;
        }
        onPaymentSuccess();
      } catch (err) {
        // The SERVER's sentence, not axios's. A refusal here is specific and actionable — "pro has
        // no list price and cannot be self-served" means the plan catalogue has no Stripe price yet
        // — and showing "Something went wrong" instead turns a one-line diagnosis into a hunt. A
        // dropped connection or a runtime error has no such sentence, so it gets the payment copy
        // rather than axios's "Network Error" (GFG-427).
        setError(userFacingErrorMessage(err, GENERIC_PAYMENT_ERROR));
      }
    },
    [plan, offer, upgrade, onPaymentSuccess],
  );

  if (!isOpen || !plan) return null;

  const planName = PLAN_DISPLAY[plan].name;
  const charge = offer ? formatPlanCharge(offer) : "—";

  return (
    <>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: decorative click-outside-to-close overlay. */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: Escape is handled by the drawer's own controls. */}
      <div onClick={handleClose} className="fixed inset-0 z-[1300] bg-black/50" />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-payment-title"
        className="fixed inset-x-0 bottom-0 z-[1301] flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white font-lato md:inset-x-auto md:top-0 md:right-0 md:bottom-0 md:h-full md:max-h-none md:w-[520px] md:rounded-none"
      >
        <div className="flex items-center justify-between border-b border-smoke p-4">
          <h2 id="onboarding-payment-title" className="m-0 font-crimson text-[22px]">
            {PLAN_DRAWER_STRINGS.title(planName)}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={isProcessing}
            aria-label="Close"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-black/[54%] hover:bg-black/[4%] disabled:cursor-default disabled:text-black/[26%]"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="flex flex-col gap-4 overflow-y-auto p-4">
          <BillingIntervalPills
            offers={offers}
            value={offer?.interval ?? interval}
            onChange={onIntervalChange}
            disabled={isProcessing}
          />
          <div className="rounded-card border border-smoke p-4">
            <div className="flex justify-between py-1">
              <p className="text-[13px] text-graphite">
                {PLAN_DRAWER_STRINGS.orderLineName(PLAN_DISPLAY[plan].badgeLabel)}
              </p>
              <p className="text-[13px]">{charge}</p>
            </div>
            <div className="flex justify-between py-1">
              <p className="text-[13px] text-graphite">{PLAN_DRAWER_STRINGS.billingPeriodLabel}</p>
              <p className="text-[13px]">
                {offer ? PLAN_INTERVAL_STRINGS.pill[offer.interval] : "—"}
              </p>
            </div>
            <div className="mt-1 flex justify-between border-t border-smoke pt-2">
              <p className="text-[14px] font-bold">{PLAN_DRAWER_STRINGS.totalLabel}</p>
              <p className="text-[14px] font-bold">{charge}</p>
            </div>
          </div>

          <div>
            <p className="mb-2 text-[14px] font-semibold uppercase">
              {PLAN_DRAWER_STRINGS.paymentMethodLabel}
            </p>
            {isProcessing ? (
              <div
                role="status"
                className="flex items-center gap-3 rounded-card border border-smoke p-4"
              >
                <CircularProgress
                  size={18}
                  thickness={4}
                  aria-hidden="true"
                  sx={{ color: "var(--color-dark-dust)" }}
                />
                <p className="text-[13px] text-graphite">
                  Charging your card and activating {planName}…
                </p>
              </div>
            ) : !offer ? (
              // No price to charge yet: the catalogue is still loading, or this plan has no offer
              // on sale. A card form here would save a card and then have nothing to buy.
              <p role="status" className="text-[13px] text-graphite">
                {plansLoading ? "Loading prices…" : `${planName} is not on sale right now.`}
              </p>
            ) : (
              <SubscriptionCardForm
                onSaved={handleCardSaved}
                submitLabel="Subscribe now"
                submittingLabel="Subscribing…"
              />
            )}
          </div>

          {error && (
            <p role="alert" className="text-[13px] text-[#c00]">
              {error}
            </p>
          )}

          <p className="flex items-center justify-center gap-1 text-center text-[12px] text-graphite">
            <LockOutlinedIcon className="text-[16px]" />
            Your card is processed securely by Stripe. We never see or store the card number.
          </p>
        </div>
      </div>
    </>
  );
}
